import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "./ThemeProvider";
import adveloLogoDarkAvif from "@/assets/optimized/e377968ce0781b86336a0c308f03923e761eabb3.avif";
import adveloLogoDarkWebp from "@/assets/optimized/e377968ce0781b86336a0c308f03923e761eabb3.webp";
import adveloLogoDarkPng from "@/assets/e377968ce0781b86336a0c308f03923e761eabb3.png";
import adveloLogoLightAvif from "@/assets/optimized/473255efd3132647e7de3da99b12aad71a28cb72.avif";
import adveloLogoLightWebp from "@/assets/optimized/473255efd3132647e7de3da99b12aad71a28cb72.webp";
import adveloLogoLightPng from "@/assets/473255efd3132647e7de3da99b12aad71a28cb72.png";

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isLightTheme = theme === 'light';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
      setIsMenuOpen(false);
      return;
    }

    setIsMenuOpen(false);
    try {
      sessionStorage.setItem('advelo-scroll-target', sectionId);
    } catch (error) {
      console.warn('Неуспешно записване на целевата секция в sessionStorage', error);
    }
    navigate('/', { state: { scrollTo: sectionId, triggerAt: Date.now() } });
  };

  const navItems = [
    { label: "Начало", id: "hero" },
    { label: "Статистики", id: "statistics" },
    { label: "Ценообразуване", id: "pricing" },
    { label: "Предимства", id: "features" },
    { label: "Контакти", id: "contact" }
  ];

  return (
    <>
      {/* Skip link for screen readers */}
      <a 
        href="#main-content" 
        className="skip-link"
      >
        Към основното съдържание
      </a>
      
      <header className="sticky top-0 z-50 w-full backdrop-blur-md bg-background/80 border-b border-border/50 transition-all duration-300">
      <div className="container-responsive relative">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          
          {/* Logo */}
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => scrollToSection('hero')}
              className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 rounded-md"
              aria-label="Advelo начало"
            >
              {(() => {
                const sources = theme === 'light'
                  ? {
                      avif: adveloLogoLightAvif,
                      webp: adveloLogoLightWebp,
                      png: adveloLogoLightPng,
                    }
                  : {
                      avif: adveloLogoDarkAvif,
                      webp: adveloLogoDarkWebp,
                      png: adveloLogoDarkPng,
                    };
                return (
                  <picture>
                    <source srcSet={sources.avif} type="image/avif" />
                    <source srcSet={sources.webp} type="image/webp" />
                    <img 
                      src={sources.png} 
                      alt="Advelo - Мобилна LED реклама" 
                      className="h-8 lg:h-10 w-auto"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                );
              })()}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                className="header-nav-link px-4 py-2 rounded-lg text-sm font-medium text-foreground/80 dark:text-foreground transition-colors duration-200 hover:text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Theme Toggle & Mobile Menu */}
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              className="lg:hidden w-10 h-10 rounded-lg border border-white/25 bg-black/30 text-white backdrop-blur-2xl shadow-[0_14px_40px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.22)] transition-all duration-300 hover:bg-black/40 hover:border-white/40 focus-visible:ring-2 focus-visible:ring-primary/60 light:bg-black/55 light:border-black/30"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <>
            <div
              className={`lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 backdrop-blur-lg ${isLightTheme ? 'bg-white/85' : 'bg-black/85'}`}
              aria-hidden="true"
              onClick={() => setIsMenuOpen(false)}
            ></div>
            <div className="lg:hidden absolute top-full left-0 right-0 z-50">
              <div className={`backdrop-blur-xl border-b border-border/40 shadow-2xl ${isLightTheme ? 'bg-white/95' : 'bg-black/90'}`}>
                <nav className="container-responsive py-6">
                  <div className="flex flex-col space-y-1.5">
                    {navItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => scrollToSection(item.id)}
                        className="header-nav-link w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-foreground transition-colors duration-200 hover:text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
    </>
  );
}